import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, ExternalLink, Settings2 } from "lucide-react";
import { toast } from "sonner";
import type { ShopEvent } from "@ticketnjoy/types";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useShopEventsQuery } from "@/features/shop-events/api";
import { useT } from "@/lib/i18n";

/**
 * Embed tipi mağazanın kod paneli. Üstte tek kodla tüm etkinlikleri listeleyen
 * embed; altında listelenen her etkinlik için kendi kodu ("Kodu kopyala") ve
 * etkinliğin embed ayarlarına kısayol ("Ayarlar" → etkinlik embed sekmesi).
 *
 * Loader shop projesinin static asset'i: `${VITE_SHOP_BASE_URL}/v1/embed.js`.
 */
export function EmbedCodeCard({ shopSlug }: { shopSlug: string }) {
  const t = useT("shops.embedCodes");
  const eventsQuery = useShopEventsQuery(shopSlug);
  const listedEvents = useMemo(
    () => (eventsQuery.data ?? []).filter((e) => e.status === "Listed"),
    [eventsQuery.data],
  );

  const shopBaseUrl = import.meta.env.VITE_SHOP_BASE_URL ?? "http://localhost:3002";
  const loaderUrl = `${shopBaseUrl}/v1/embed.js`;
  const allEventsSnippet = buildSnippet(loaderUrl, shopSlug, null);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t("title")}</CardTitle>
        <CardDescription>{t("description")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <section className="space-y-2">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold">{t("allTitle")}</h3>
              <p className="text-xs text-muted-foreground">{t("allDescription")}</p>
            </div>
            <div className="flex items-center gap-2">
              <CopyButton text={allEventsSnippet} />
              <Button type="button" size="sm" variant="ghost" asChild>
                <a
                  href={`${shopBaseUrl}/embed/${shopSlug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="me-1.5 h-3.5 w-3.5" />
                  {t("preview")}
                </a>
              </Button>
            </div>
          </div>
          <textarea
            readOnly
            value={allEventsSnippet}
            dir="ltr"
            spellCheck={false}
            rows={2}
            onFocus={(e) => e.currentTarget.select()}
            aria-label={t("allTitle")}
            className="w-full resize-none rounded-md border bg-muted/30 p-3 font-mono text-xs"
          />
        </section>

        <section className="space-y-2">
          <div>
            <h3 className="text-sm font-semibold">{t("perEventTitle")}</h3>
            <p className="text-xs text-muted-foreground">{t("perEventDescription")}</p>
          </div>
          {listedEvents.length === 0 ? (
            <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
              {t("empty")}
            </p>
          ) : (
            <ul className="divide-y rounded-md border">
              {listedEvents.map((ev) => (
                <EventRow
                  key={ev.id}
                  event={ev}
                  snippet={buildSnippet(loaderUrl, shopSlug, ev.eventEmbedSlug ?? ev.eventSlug)}
                />
              ))}
            </ul>
          )}
        </section>
      </CardContent>
    </Card>
  );
}

function EventRow({ event, snippet }: { event: ShopEvent; snippet: string }) {
  const t = useT("shops.embedCodes");
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{event.eventTitle}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="font-mono" dir="ltr">
            {event.eventEmbedSlug ?? event.eventSlug}
          </span>
          {event.eventEmbedEnabled ? null : (
            <Badge variant="outline" className="text-[10px]">
              {t("embedOff")}
            </Badge>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <CopyButton text={snippet} label={t("copyCode")} />
        <Button type="button" size="sm" variant="ghost" asChild>
          <Link to={`/events/${event.eventSlug}?tab=embed`}>
            <Settings2 className="me-1.5 h-3.5 w-3.5" />
            {t("settings")}
          </Link>
        </Button>
      </div>
    </li>
  );
}

function CopyButton({ text, label }: { text: string; label?: string }) {
  const tCommon = useT("common");
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(tCommon("copyFailed"));
    }
  }

  return (
    <Button type="button" size="sm" variant="outline" onClick={handleCopy}>
      {copied ? <Check className="me-1.5 h-3.5 w-3.5" /> : <Copy className="me-1.5 h-3.5 w-3.5" />}
      {copied ? tCommon("copied") : (label ?? tCommon("copy"))}
    </Button>
  );
}

function buildSnippet(loader: string, shop: string, event: string | null): string {
  const eventAttr = event ? ` data-ticketnjoy-event="${event}"` : "";
  return `<div data-ticketnjoy-shop="${shop}"${eventAttr}></div>
<script src="${loader}" async></script>`;
}
